import { apiRequest } from "@/lib/api/client";
import { getAuthToken } from "@/lib/api/authToken";
import { getApiBaseUrl } from "@/lib/api/env";
import { ApiHttpError } from "@/lib/api/errors";
import type { AssetDto, AssetKind } from "@/lib/api/types";

export type UploadUrlResponse = {
  storageKey: string;
  uploadUrl: string;
  mimeType: string;
  kind: string;
};

export async function listAssets(): Promise<AssetDto[]> {
  return apiRequest<AssetDto[]>("/assets");
}

export async function getAsset(id: string): Promise<AssetDto> {
  return apiRequest<AssetDto>(`/assets/${encodeURIComponent(id)}`);
}

/** Public URL that serves the asset bytes (CDN or API file route). */
export function getAssetFileUrl(asset: AssetDto | string): string {
  const id = typeof asset === "string" ? asset : asset.id;
  if (typeof asset !== "string" && asset.url && isUsablePublicAssetUrl(asset.url)) {
    return asset.url;
  }
  return `${getApiBaseUrl()}/assets/${encodeURIComponent(id)}/file`;
}

/**
 * Resolve a display URL from persisted image block props.
 * Prefers `assetId` + current API base so localhost / wrong-host URLs from Save still work in prod.
 */
export function resolveImageBlockUrl(props: {
  assetId?: unknown;
  url?: unknown;
}): string {
  if (typeof props.assetId === "string" && props.assetId.length > 0) {
    return getAssetFileUrl(props.assetId);
  }
  const url = typeof props.url === "string" ? props.url : "";
  if (url && isUsablePublicAssetUrl(url)) return url;
  const match = url.match(/\/assets\/([^/?#]+)\/file\/?$/i);
  if (match?.[1]) return getAssetFileUrl(match[1]);
  return url;
}

/** Reject localhost and R2 S3 API hosts — those are not browser-reachable in prod. */
export function isUsablePublicAssetUrl(url: string): boolean {
  try {
    const u = new URL(url);
    if (u.hostname === "localhost" || u.hostname === "127.0.0.1") return false;
    if (u.hostname.includes("r2.cloudflarestorage.com")) return false;
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}


export async function requestUploadUrl(input: {
  mimeType: string;
  kind: AssetKind;
}): Promise<UploadUrlResponse> {
  return apiRequest<UploadUrlResponse>("/assets/upload-url", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function uploadBytes(
  uploadUrl: string,
  data: ArrayBuffer,
  mimeType: string,
): Promise<{ bytesWritten: number }> {
  const token = await getAuthToken();
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": mimeType,
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: data,
  });
  const text = await res.text();
  let body: unknown = undefined;
  if (text) {
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      body = text;
    }
  }
  if (!res.ok) {
    throw new ApiHttpError(res.status, body);
  }
  return body as { bytesWritten: number };
}

export async function completeUpload(input: {
  storageKey: string;
  kind: AssetKind;
  mimeType: string;
  byteSize: number;
  width?: number;
  height?: number;
  alt?: string;
}): Promise<AssetDto> {
  return apiRequest<AssetDto>("/assets/complete", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** Uploads a song for the invitation opening (request URL → PUT → complete). */
export async function uploadAudioFile(file: File): Promise<AssetDto & { url: string }> {
  const mimeType = file.type || "audio/mpeg";
  const kind: AssetKind = "AUDIO";
  const { storageKey, uploadUrl } = await requestUploadUrl({ mimeType, kind });
  const buffer = await file.arrayBuffer();
  await uploadBytes(uploadUrl, buffer, mimeType);
  const asset = await completeUpload({
    storageKey,
    kind,
    mimeType,
    byteSize: buffer.byteLength,
    alt: file.name || undefined,
  });
  return { ...asset, url: getAssetFileUrl(asset) };
}

/**
 * Full pipeline: request URL → PUT bytes → complete → asset with display `url`.
 */
export async function uploadImageFile(file: File): Promise<AssetDto & { url: string }> {
  const mimeType = file.type || "application/octet-stream";
  const kind: AssetKind = "IMAGE";
  const { storageKey, uploadUrl } = await requestUploadUrl({ mimeType, kind });
  const buffer = await file.arrayBuffer();
  await uploadBytes(uploadUrl, buffer, mimeType);

  let width: number | undefined;
  let height: number | undefined;
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file);
      width = bitmap.width;
      height = bitmap.height;
      bitmap.close();
    } catch {
      // Dimensions optional on complete.
    }
  }

  const asset = await completeUpload({
    storageKey,
    kind,
    mimeType,
    byteSize: buffer.byteLength,
    width,
    height,
    alt: file.name || undefined,
  });
  return { ...asset, url: getAssetFileUrl(asset) };
}
